import React, { Fragment, useId, useMemo, useState } from "react";

import {
  fill,
  formatMoney,
  getVariantImage,
  getVariantLabel,
  getVariantOptionValue,
  getVariantStock,
  summarizePrices,
  variantDraftHasUnsavedChanges,
} from "../studioEditModel";
import {
  Btn,
  Card,
  Chip,
  EmptyState,
  Field,
  Icon,
  Menu,
  MoneyInput,
  Pill,
  Select,
  Swatch,
  TextInput,
  Thumb,
  cx,
} from "../StudioUi";
import StructureEditorDrawer from "../drawers/StructureEditorDrawer";


/** Case/diacritic-insensitive search haystack for one variant. */
const buildHaystack = (variant, draft) =>
  [getVariantLabel(variant), variant.title, draft?.sku, draft?.barcode, variant.sku, variant.barcode]
    .filter(Boolean)
    .join(" ")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const normalizeQuery = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const VariantsTab = ({ studio }) => {
  const {
    tt,
    workspace,
    liveVariants,
    archivedVariants,
    variantDrafts,
    liveVariantDrafts,
    mediaDrafts,
    colorOption,
    colorRecords,
    inventoryMode,
    dirty,
    updateVariant,
    setVariantStatusBulk,
    selectTab,
    openPrint,
    setFocusVariantId,
  } = studio;
  const ids = useId();
  const options = useMemo(() => workspace.options || [], [workspace.options]);
  const [query, setQuery] = useState("");
  const [optionFilters, setOptionFilters] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const draftById = useMemo(() => new Map(variantDrafts.map((draft) => [draft.id, draft])), [variantDrafts]);
  const sourceById = useMemo(
    () => new Map((workspace.variants || []).map((variant) => [variant.id, variant])),
    [workspace.variants]
  );

  const rows = useMemo(() => {
    const normalized = normalizeQuery(query);
    return liveVariants.filter((variant) => {
      const draft = draftById.get(variant.id);
      if (normalized && !buildHaystack(variant, draft).includes(normalized)) return false;
      if (statusFilter && (draft?.status || variant.status) !== statusFilter) return false;
      return options.every((option) => {
        const wanted = optionFilters[option.id];
        return !wanted || getVariantOptionValue(variant, option) === wanted;
      });
    });
  }, [draftById, liveVariants, optionFilters, options, query, statusFilter]);

  const printableIds = liveVariants.filter((variant) => variant.barcode).map((variant) => variant.id);
  const selectedPrintable = selectedIds.filter((id) => printableIds.includes(id));
  const allVisibleSelected = rows.length > 0 && rows.every((variant) => selectedIds.includes(variant.id));
  const hasFilters = Boolean(query || statusFilter || Object.values(optionFilters).some(Boolean));

  const toggleSelected = (variantId) =>
    setSelectedIds((current) =>
      current.includes(variantId) ? current.filter((id) => id !== variantId) : [...current, variantId]
    );
  const toggleAllVisible = () =>
    setSelectedIds((current) =>
      allVisibleSelected
        ? current.filter((id) => !rows.some((variant) => variant.id === id))
        : Array.from(new Set([...current, ...rows.map((variant) => variant.id)]))
    );

  const priceSummary = summarizePrices(liveVariantDrafts);
  const uniqueSkus = new Set(liveVariantDrafts.map((draft) => draft.sku).filter(Boolean)).size;
  const withBarcode = liveVariantDrafts.filter((draft) => draft.barcode).length;
  const totalAvailable = liveVariants.reduce((sum, variant) => sum + getVariantStock(variant, inventoryMode).available, 0);
  const isVariantProduct = workspace.product.productMode === "variant";
  const optionCounts = options.map((option) => `${option.values.length} ${option.name.toLowerCase()}`);

  const statusOptions = [
    { value: "active", label: tt("vrStatusActive") },
    { value: "draft", label: tt("vrStatusDraft") },
  ];

  return (
    <div className="pse-layout">
      <div className="pse-layout__main">
        <Card
          title={tt("vrTitle")}
          subtitle={workspace.product.productMode === "variant" ? tt("vrCopy") : tt("vrStructureSimple")}
          actions={
            <>
              {dirty.variants || dirty.pricing ? <Chip tone="warning">{tt("unsavedChip")}</Chip> : null}
              <Btn variant="secondary" icon="refresh" onClick={() => studio.setIsStructureEditorOpen(true)}>
                {tt("vrChangeStructure")}
              </Btn>
            </>
          }
        >
          {!liveVariants.length ? (
            <EmptyState
              icon="variants"
              title={tt("vrEmptyTitle")}
              action={
                <Btn variant="primary" icon="plus" onClick={() => studio.setIsStructureEditorOpen(true)}>
                  {tt("vrChangeStructure")}
                </Btn>
              }
            >
              {tt("vrEmptyCopy")}
            </EmptyState>
          ) : (
            <>
              <div className="pse-toolbar">
                <label className="pse-search">
                  <Icon name="search" size={17} />
                  <input
                    type="search"
                    value={query}
                    placeholder={tt("vrSearch")}
                    aria-label={tt("vrSearch")}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </label>
                {options.map((option) => (
                  <Select
                    key={option.id}
                    aria-label={fill(tt("vrAllValues"), { name: option.name })}
                    value={optionFilters[option.id] || ""}
                    onChange={(value) => setOptionFilters((current) => ({ ...current, [option.id]: value }))}
                  >
                    <option value="">{fill(tt("vrAllValues"), { name: option.name })}</option>
                    {option.values.map((value) => (
                      <option key={value.id} value={value.value}>
                        {value.value}
                      </option>
                    ))}
                  </Select>
                ))}
                <Select aria-label={tt("vrAllStatuses")} value={statusFilter} onChange={setStatusFilter}>
                  <option value="">{tt("vrAllStatuses")}</option>
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>

              {selectedIds.length ? (
                <div className="pse-selection-bar" role="region" aria-live="polite">
                  <strong>{fill(tt("vrSelected"), { count: selectedIds.length })}</strong>
                  <div className="pse-selection-bar__actions">
                    <Btn
                      variant="secondary"
                      size="sm"
                      icon="printer"
                      disabled={!selectedPrintable.length}
                      title={!selectedPrintable.length ? tt("vrNoBarcode") : undefined}
                      onClick={() => openPrint(selectedPrintable)}
                    >
                      {fill(tt("vrPrintSelected"), { count: selectedPrintable.length })}
                    </Btn>
                    <Menu
                      variant="button"
                      icon="sliders"
                      buttonLabel={tt("vrSetStatusSelected")}
                      label={tt("vrSetStatusSelected")}
                      items={statusOptions.map((option) => ({
                        label: option.label,
                        onClick: () => setVariantStatusBulk(selectedIds, option.value),
                      }))}
                    />
                    <Btn variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
                      {tt("vrClearSelection")}
                    </Btn>
                  </div>
                </div>
              ) : null}

              {rows.length ? (
                <div className="pse-table-wrap">
                  <table className="pse-table pse-table--variants">
                    <thead>
                      <tr>
                        <th className="pse-table__check">
                          <input
                            type="checkbox"
                            aria-label={tt("vrSelectAll")}
                            checked={allVisibleSelected}
                            onChange={toggleAllVisible}
                          />
                        </th>
                        <th>{tt("vrColVariant")}</th>
                        {options.map((option) => (
                          <th key={option.id} className="pse-hide-lg">
                            {option.name}
                          </th>
                        ))}
                        <th className="pse-table__num">{tt("vrColPrice")}</th>
                        <th className="pse-table__num">{tt("vrColStock")}</th>
                        <th>{tt("vrColStatus")}</th>
                        <th className="pse-table__actions">
                          <span className="sr-only">{tt("vrColActions")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((variant) => {
                        const draft = draftById.get(variant.id) || {};
                        const label = getVariantLabel(variant) || fill(tt("variantFallback"), { id: variant.id });
                        const image = getVariantImage(variant, mediaDrafts, colorOption);
                        const stock = getVariantStock(variant, inventoryMode);
                        const isExpanded = expandedId === variant.id;
                        const isChanged = variantDraftHasUnsavedChanges(draft, sourceById.get(variant.id));
                        return (
                          <Fragment key={variant.id}>
                            <tr className={cx("pse-row", isChanged && "is-changed", isExpanded && "is-expanded")}>
                              <td className="pse-table__check">
                                <input
                                  type="checkbox"
                                  aria-label={label}
                                  checked={selectedIds.includes(variant.id)}
                                  onChange={() => toggleSelected(variant.id)}
                                />
                              </td>
                              <td className="pse-cell-variant">
                                <Thumb src={image?.sourceUrl} size={46} alt="" />
                                <div className="pse-cell-variant__text">
                                  <span className="pse-cell-variant__label">
                                    {label}
                                    {draft.isDefault ? (
                                      <Chip tone="info" title={tt("vrDefaultHint")}>
                                        <Icon name="star" size={11} strokeWidth={2.4} /> {tt("vrDefault")}
                                      </Chip>
                                    ) : null}
                                    {isChanged ? <Chip tone="warning">{tt("vrNotSaved")}</Chip> : null}
                                  </span>
                                  <span className="pse-cell-variant__meta">
                                    <span title={tt("vrSku")}>{draft.sku || "—"}</span>
                                    {draft.barcode ? (
                                      <span title={tt("vrBarcode")}>
                                        <Icon name="barcode" size={12} /> {draft.barcode}
                                      </span>
                                    ) : null}
                                  </span>
                                </div>
                              </td>
                              {options.map((option) => {
                                const value = getVariantOptionValue(variant, option);
                                const isColor = colorOption && option.id === colorOption.id;
                                return (
                                  <td key={option.id} className="pse-hide-lg" data-label={option.name}>
                                    <span className="pse-inline">
                                      {isColor && value ? <Swatch value={value} colorRecords={colorRecords} /> : null}
                                      {value || "—"}
                                    </span>
                                  </td>
                                );
                              })}
                              <td className="pse-table__num" data-label={tt("vrColPrice")}>
                                <MoneyInput
                                  value={draft.price}
                                  aria-label={`${tt("vrColPrice")} · ${label}`}
                                  onChange={(value) => updateVariant(variant.id, "price", value)}
                                  className="pse-input pse-money__input pse-input--compact"
                                />
                              </td>
                              <td className="pse-table__num" data-label={tt("vrColStock")}>
                                <button
                                  type="button"
                                  className={cx("pse-stock-link", `is-${stock.level}`)}
                                  title={tt("vrViewStock")}
                                  onClick={() => {
                                    setFocusVariantId(variant.id);
                                    selectTab("inventory");
                                  }}
                                >
                                  {stock.available}
                                </button>
                              </td>
                              <td data-label={tt("vrColStatus")}>
                                <Select
                                  className={cx("pse-select--pill", `is-${draft.status}`)}
                                  aria-label={`${tt("vrColStatus")} · ${label}`}
                                  value={draft.status}
                                  onChange={(value) => updateVariant(variant.id, "status", value)}
                                >
                                  {statusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Select>
                              </td>
                              <td className="pse-table__actions">
                                <div className="pse-row-actions">
                                  <button
                                    type="button"
                                    className="pse-icon-btn"
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded ? tt("vrHideDetails") : tt("vrDetails")}
                                    title={isExpanded ? tt("vrHideDetails") : tt("vrDetails")}
                                    onClick={() => setExpandedId(isExpanded ? null : variant.id)}
                                  >
                                    <Icon name={isExpanded ? "chevronDown" : "edit"} size={17} />
                                  </button>
                                  <Menu
                                    label={tt("vrColActions")}
                                    items={[
                                      {
                                        label: isExpanded ? tt("vrHideDetails") : tt("vrDetails"),
                                        icon: "edit",
                                        onClick: () => setExpandedId(isExpanded ? null : variant.id),
                                      },
                                      !draft.isDefault
                                        ? {
                                            label: tt("vrMakeDefault"),
                                            icon: "star",
                                            onClick: () => updateVariant(variant.id, "isDefault", true),
                                          }
                                        : null,
                                      {
                                        label: variant.barcode ? tt("vrPrintLabel") : tt("vrNoBarcode"),
                                        icon: "printer",
                                        disabled: !variant.barcode,
                                        onClick: () => openPrint([variant.id]),
                                      },
                                      {
                                        label: tt("vrViewStock"),
                                        icon: "cube",
                                        onClick: () => {
                                          setFocusVariantId(variant.id);
                                          selectTab("inventory");
                                        },
                                      },
                                    ]}
                                  />
                                </div>
                              </td>
                            </tr>
                            {isExpanded ? (
                              <tr className="pse-row-detail">
                                <td colSpan={6 + options.length}>
                                  <div className="pse-row-detail__grid">
                                    <Field label={tt("vrSku")} htmlFor={`${ids}-sku-${variant.id}`}>
                                      <TextInput
                                        id={`${ids}-sku-${variant.id}`}
                                        value={draft.sku}
                                        onChange={(value) => updateVariant(variant.id, "sku", value)}
                                      />
                                    </Field>
                                    <Field label={tt("vrBarcode")} htmlFor={`${ids}-barcode-${variant.id}`}>
                                      <TextInput
                                        id={`${ids}-barcode-${variant.id}`}
                                        value={draft.barcode}
                                        placeholder={tt("vrBarcodePlaceholder")}
                                        onChange={(value) => updateVariant(variant.id, "barcode", value)}
                                      />
                                    </Field>
                                    <Field label={tt("vrCompareAt")} htmlFor={`${ids}-compare-${variant.id}`}>
                                      <MoneyInput
                                        id={`${ids}-compare-${variant.id}`}
                                        value={draft.compareAtPrice}
                                        placeholder={tt("optional")}
                                        onChange={(value) => updateVariant(variant.id, "compareAtPrice", value)}
                                      />
                                    </Field>
                                    <Field label={tt("vrCost")} htmlFor={`${ids}-cost-${variant.id}`}>
                                      <MoneyInput
                                        id={`${ids}-cost-${variant.id}`}
                                        value={draft.cost}
                                        placeholder={tt("optional")}
                                        onChange={(value) => updateVariant(variant.id, "cost", value)}
                                      />
                                    </Field>
                                    <div className="pse-row-detail__default">
                                      <button
                                        type="button"
                                        className={cx("pse-default-toggle", draft.isDefault && "is-on")}
                                        aria-pressed={Boolean(draft.isDefault)}
                                        onClick={() => updateVariant(variant.id, "isDefault", true)}
                                        disabled={draft.isDefault}
                                      >
                                        <Icon name="star" size={16} />
                                        {draft.isDefault ? tt("vrDefault") : tt("vrMakeDefault")}
                                      </button>
                                      <span className="pse-muted pse-small">{tt("vrDefaultHint")}</span>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  icon="search"
                  title={tt("vrNoMatch")}
                  action={
                    <Btn
                      variant="secondary"
                      onClick={() => {
                        setQuery("");
                        setStatusFilter("");
                        setOptionFilters({});
                      }}
                    >
                      {tt("vrResetFilters")}
                    </Btn>
                  }
                />
              )}
              <p className="pse-table-foot">
                {hasFilters
                  ? fill(tt("vrShown"), { shown: rows.length, total: liveVariants.length })
                  : fill(tt("vrTotal"), { count: liveVariants.length })}
              </p>
            </>
          )}
        </Card>

        {archivedVariants.length ? (
          <Card
            title={fill(tt("vrArchivedTitle"), { count: archivedVariants.length })}
            subtitle={tt("vrArchivedCopy")}
            actions={
              <Btn
                variant="ghost"
                size="sm"
                iconRight={showArchived ? "chevronDown" : "chevronRight"}
                aria-expanded={showArchived}
                onClick={() => setShowArchived((current) => !current)}
              >
                {showArchived ? tt("close") : tt("showMore")}
              </Btn>
            }
          >
            {showArchived ? (
              <ul className="pse-archived-list">
                {archivedVariants.map((variant) => (
                  <li key={variant.id}>
                    <span>{getVariantLabel(variant) || fill(tt("variantFallback"), { id: variant.id })}</span>
                    <span className="pse-muted pse-small">
                      {variant.sku || "—"}
                      {variant.barcode ? ` · ${variant.barcode}` : ""}
                    </span>
                    <Pill tone="danger">{tt("vrStatusArchived")}</Pill>
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>
        ) : null}
      </div>

      <aside className="pse-layout__side">
        <Card
          title={tt("vrStructure")}
          actions={
            <Btn variant="secondary" size="sm" onClick={() => studio.setIsStructureEditorOpen(true)}>
              {tt("edit")}
            </Btn>
          }
        >
          {isVariantProduct && options.length ? (
            <>
              <ul className="pse-structure-list">
                {options.map((option) => (
                  <li key={option.id}>
                    <strong>
                      {option.name} ({option.values.length})
                    </strong>
                    <span className="pse-chip-row">
                      {option.values.map((value) => (
                        <Chip key={value.id}>
                          {colorOption && option.id === colorOption.id ? (
                            <Swatch value={value.value} colorRecords={colorRecords} size={11} />
                          ) : null}
                          {value.value}
                        </Chip>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="pse-formula">
                <Icon name="checkCircle" size={20} />
                <span>
                  {workspace.product.generationMode === "manual"
                    ? fill(tt("vrStructureManual"), { count: liveVariants.length })
                    : fill(tt("vrStructureFormula"), {
                        parts: optionCounts.join(" × "),
                        count: liveVariants.length,
                      })}
                </span>
              </div>
            </>
          ) : (
            <p className="pse-muted">{tt("vrStructureSimple")}</p>
          )}
        </Card>

        {liveVariantDrafts.length ? (
          <Card title={tt("vrPricing")}>
            <div className="pse-kv-list">
              <div className="pse-kv">
                <span className="pse-kv__label">{priceSummary.same ? tt("vrPricingSame") : tt("vrPricingMixed")}</span>
                <span className="pse-kv__value">
                  {priceSummary.min === null
                    ? "—"
                    : priceSummary.same
                    ? formatMoney(priceSummary.min)
                    : `${formatMoney(priceSummary.min)} – ${formatMoney(priceSummary.max)}`}
                </span>
              </div>
            </div>
            <Btn variant="secondary" block icon="edit" onClick={() => selectTab("overview")}>
              {tt("vrPricingEdit")}
            </Btn>
          </Card>
        ) : null}

        <Card title={tt("vrQuickStats")}>
          <div className="pse-mini-stats pse-mini-stats--3">
            <div className="pse-mini-stat">
              <strong>{liveVariants.length}</strong>
              <span>{tt("vrStatVariants")}</span>
            </div>
            <div className="pse-mini-stat pse-mini-stat--success">
              <strong>{totalAvailable}</strong>
              <span>{tt("vrStatStock")}</span>
            </div>
            <div className="pse-mini-stat">
              <strong>{uniqueSkus}</strong>
              <span>{tt("vrStatSkus")}</span>
            </div>
            <div className="pse-mini-stat">
              <strong>{withBarcode}</strong>
              <span>{tt("vrStatBarcodes")}</span>
            </div>
            {options.map((option) => (
              <div key={option.id} className="pse-mini-stat">
                <strong>{option.values.length}</strong>
                <span>{option.name}</span>
              </div>
            ))}
          </div>
        </Card>
      </aside>

      <StructureEditorDrawer studio={studio} />
    </div>
  );
};

export default VariantsTab;
